/**
 * Breakout's routes (ACP-D47, docs/gfx-breakout/plan.md §3; spread into `ROUTES` by `routes.ts`): a designer opens a
 * session, hands out its capability link, and manages the screens that joined through it. The stream itself runs on
 * the relay's own WebSocket API (`breakout.ts`, docs/gfx-breakout/relay.md), not here. Cloud only.
 *
 * Every route needs `graphics.breakout` tenant-wide, and a session is its owner's alone: another user of the tenant
 * (even one holding the permission) reads every session id but their own as `not-found`, so ids reveal nothing. The
 * staff tenant (D39) is refused the writes (`graphics` tag, `staffTenant.ts`).
 *
 * The caps (Q8 as decided, in the relay's config): 20 remotes per session, 5 open sessions per user, 25 per tenant.
 * A session lasts at most 12 hours.
 */
import {
  breakoutRemotePathWireSchema,
  breakoutSessionDetailWireSchema,
  breakoutSessionPathWireSchema,
  closeBreakoutSessionResponseWireSchema,
  disconnectBreakoutRemoteResponseWireSchema,
  listBreakoutRemotesResponseWireSchema,
  listBreakoutSessionsResponseWireSchema,
  openBreakoutSessionRequestWireSchema,
  openBreakoutSessionResponseWireSchema,
  setBreakoutLinkRequestWireSchema,
  setBreakoutLinkResponseWireSchema,
} from './breakout.js';
import type { RouteDef } from './routes.js';

/** The subprotocol a breakout client offers (`Sec-WebSocket-Protocol: acp.breakout.v1`); the relay echoes it. ACP's name, given to the relay. */
export const BREAKOUT_SUBPROTOCOL = 'acp.breakout.v1';
/** Where the session routes live (every path above begins with it); the relay's dev server serves them there too. */
export const BREAKOUT_PATH_PREFIX = '/graphics/breakout';

const OWNER_ONLY = 'A session that is not the caller’s own — another user’s, closed, expired or unknown — is `not-found`.';

export const BREAKOUT_ROUTES = {
  openGraphicsBreakoutSession: {
    id: 'openGraphicsBreakoutSession',
    method: 'POST',
    path: '/graphics/breakout/sessions',
    tag: 'graphics',
    summary: 'Open a breakout session',
    description:
      'A new session owned by the caller, lasting at most 12 hours, with its first capability link. The link’s token is answered ONCE (the relay keeps only its hash); it goes in the player URL’s fragment. The designer then attaches to the relay with its own token and publishes the stream. `conflict` when the caller already has 5 open sessions or the tenant 25.',
    params: null,
    query: null,
    body: openBreakoutSessionRequestWireSchema,
    response: openBreakoutSessionResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict'],
  },
  listGraphicsBreakoutSessions: {
    id: 'listGraphicsBreakoutSessions',
    method: 'GET',
    path: '/graphics/breakout/sessions',
    tag: 'graphics',
    summary: 'List the caller’s breakout sessions',
    description: 'The caller’s open sessions, newest first. Other users’ sessions are not listed.',
    params: null,
    query: null,
    body: null,
    response: listBreakoutSessionsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: [],
  },
  getGraphicsBreakoutSession: {
    id: 'getGraphicsBreakoutSession',
    method: 'GET',
    path: '/graphics/breakout/sessions/{sessionId}',
    tag: 'graphics',
    summary: 'Get a breakout session',
    description: `The session and its remotes: each one’s last report (size, aspect class, fps, clock offset, latency). ${OWNER_ONLY}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: null,
    response: breakoutSessionDetailWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  setGraphicsBreakoutLink: {
    id: 'setGraphicsBreakoutLink',
    method: 'POST',
    path: '/graphics/breakout/sessions/{sessionId}/link',
    tag: 'graphics',
    summary: 'Make a new link, or stop admitting screens',
    description: `\`admit: true\` makes a new capability link and answers its token once; the previous link stops admitting at once. \`admit: false\` is “no new screens”: no link admits anyone until a new one is made. Screens already joined are not affected (disconnect them one by one). ${OWNER_ONLY}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: setBreakoutLinkRequestWireSchema,
    response: setBreakoutLinkResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found'],
  },
  listGraphicsBreakoutRemotes: {
    id: 'listGraphicsBreakoutRemotes',
    method: 'GET',
    path: '/graphics/breakout/sessions/{sessionId}/remotes',
    tag: 'graphics',
    summary: 'List a session’s remotes',
    description: `The screens that joined the session and have not been disconnected, with whether each is connected now and its last report. ${OWNER_ONLY}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: null,
    response: listBreakoutRemotesResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  disconnectGraphicsBreakoutRemote: {
    id: 'disconnectGraphicsBreakoutRemote',
    method: 'DELETE',
    path: '/graphics/breakout/sessions/{sessionId}/remotes/{remoteId}',
    tag: 'graphics',
    summary: 'Disconnect one remote',
    description: `Revokes the remote’s credential and closes its socket (it is told \`revoked\` first); it receives nothing further. Its link still admits a new join unless the link was renewed or admitting stopped. ${OWNER_ONLY} An unknown remote is \`not-found\`.`,
    params: breakoutRemotePathWireSchema,
    query: null,
    body: null,
    response: disconnectBreakoutRemoteResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  closeGraphicsBreakoutSession: {
    id: 'closeGraphicsBreakoutSession',
    method: 'DELETE',
    path: '/graphics/breakout/sessions/{sessionId}',
    tag: 'graphics',
    summary: 'Close a breakout session',
    description: `Revokes the link and every credential, tells every socket \`closed\` and closes it, and forgets the session. ${OWNER_ONLY}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: null,
    response: closeBreakoutSessionResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.breakout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Record<string, RouteDef>;
